import { ref, computed, readonly } from 'vue'
import type { ClusterFormData } from '~/types/cluster'

type ClusterBuildingState = 
  | 'idle'
  | 'askName'
  | 'askEmail'
  | 'askRequirements'
  | 'askGpuCount'
  | 'askDuration'
  | 'askProject'
  | 'confirm'
  | 'submitting'
  | 'completed'
  | 'cancelled'

export const useClusterBuildingStateMachine = () => {
  const { t } = useI18n()
  
  const currentState = ref<ClusterBuildingState>('idle')
  const isEditingFromConfirm = ref(false)
  const formData = ref<ClusterFormData>({
    name: '',
    email: '',
    requirements: '',
    gpuCount: null,
    duration: '',
    projectDetails: ''
  })
  
  const stateToFieldMap: Record<ClusterBuildingState, keyof ClusterFormData | null> = {
    'idle': null,
    'askName': 'name',
    'askEmail': 'email',
    'askRequirements': 'requirements',
    'askGpuCount': 'gpuCount',
    'askDuration': 'duration',
    'askProject': 'projectDetails',
    'confirm': null,
    'submitting': null,
    'completed': null,
    'cancelled': null
  }
  
  const numberToStateMap: Record<string, ClusterBuildingState> = {
    '1': 'askName',
    '2': 'askEmail',
    '3': 'askRequirements',
    '4': 'askGpuCount',
    '5': 'askDuration',
    '6': 'askProject'
  }
  
  const validateCurrentStep = (input: string): { valid: boolean; error?: string } => {
    switch (currentState.value) {
      case 'askName':
        return input.trim().length > 0 
          ? { valid: true } 
          : { valid: false, error: t('clusterBuilding.errors.nameRequired') }
          
      case 'askEmail':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(input.trim()) 
          ? { valid: true } 
          : { valid: false, error: t('clusterBuilding.errors.invalidEmail') }
          
      case 'askRequirements':
        return { valid: true }  // Optional field - allow empty
          
      case 'askGpuCount':
        const count = parseInt(input)
        return !isNaN(count) && count > 0 && count <= 2000
          ? { valid: true } 
          : { valid: false, error: t('clusterBuilding.errors.invalidGpuCount') }
          
      case 'askDuration':
        return { valid: true }  // Optional field - allow empty
          
      case 'askProject':
        return { valid: true }  // Optional field - allow empty
          
      default:
        return { valid: true }
    }
  }
  
  const saveStepData = (input: string): void => {
    const field = stateToFieldMap[currentState.value]
    if (field) {
      if (field === 'gpuCount') {
        formData.value[field] = parseInt(input)
      } else {
        formData.value[field] = input.trim()
      }
    }
  }
  
  const getNextState = (): ClusterBuildingState | null => {
    const stateFlow: ClusterBuildingState[] = [
      'askName', 'askEmail', 'askRequirements', 
      'askGpuCount', 'askDuration', 'askProject', 'confirm'
    ]
    const currentIndex = stateFlow.indexOf(currentState.value)
    if (currentIndex >= 0 && currentIndex < stateFlow.length - 1) {
      return stateFlow[currentIndex + 1]
    }
    return null
  }
  
  const transition = (newState: ClusterBuildingState): boolean => {
    currentState.value = newState
    return true
  }
  
  const nextStep = (): ClusterBuildingState | null => {
    const next = getNextState()
    if (next) {
      transition(next)
      return next
    }
    return null
  }
  
  const transitionToEditState = (identifier: string): ClusterBuildingState | null => {
    if (numberToStateMap[identifier]) {
      isEditingFromConfirm.value = true
      return numberToStateMap[identifier]
    }
    return null
  }
  
  const resetEditFlag = () => {
    isEditingFromConfirm.value = false
  }
  
  const reset = () => {
    currentState.value = 'idle'
    isEditingFromConfirm.value = false
    formData.value = {
      name: '',
      email: '',
      requirements: '',
      gpuCount: null,
      duration: '',
      projectDetails: ''
    }
  }
  
  return {
    currentState: readonly(currentState),
    formData: readonly(formData),
    isEditingFromConfirm: readonly(isEditingFromConfirm),
    transition,
    nextStep,
    validateCurrentStep,
    saveStepData,
    transitionToEditState,
    resetEditFlag,
    reset,
    isActive: computed(() => currentState.value !== 'idle' && currentState.value !== 'completed' && currentState.value !== 'cancelled'),
    isConfirmState: computed(() => currentState.value === 'confirm'),
    stateToFieldMap: readonly(stateToFieldMap)
  }
}
